import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { combineReducers } from 'redux';
import { ordersApi } from './apis/orders';
import { eventsApi } from './apis/events';
import { ticketsApi } from './apis/tickets';
import { businessesApi } from './apis/business';
import { eventsReducer } from './slices/events';
import { usersReducer } from './slices/users';
import { usersApi } from './apis/users';
import { businessReducer } from './slices/business';
import { ordersReducer } from './slices/orders';
import { ticketsReducer } from './slices/tickets';
import { couponsApi } from './apis/coupons';
import { authReducer } from './slices/auth';
import { referralsApi } from './apis/referrals';
import { commonApi } from './apis/common';
import { paymentsApi } from './apis/payment';
import { bankAccountApi } from './apis/bank-account';
import { wireApi } from './apis/wire';
import { aiQueryApi } from './apis/ai-query';
import { customerApi } from './apis/customer';
import { listApi } from './apis/lists';
import { scannerKeysApi } from './apis/scanner-keys';
import { binsApi } from './apis/bins';
import { scannerTypesApi } from './apis/scanner-types';

export const store = configureStore({
  reducer: {
    [ordersApi.reducerPath]: ordersApi.reducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [ticketsApi.reducerPath]: ticketsApi.reducer,
    [businessesApi.reducerPath]: businessesApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [couponsApi.reducerPath]: couponsApi.reducer,
    [referralsApi.reducerPath]: referralsApi.reducer,
    [commonApi.reducerPath]: commonApi.reducer,
    [paymentsApi.reducerPath]: paymentsApi.reducer,
    [bankAccountApi.reducerPath]: bankAccountApi.reducer,
    [wireApi.reducerPath]: wireApi.reducer,
    [aiQueryApi.reducerPath]: aiQueryApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [listApi.reducerPath]: listApi.reducer,
    [scannerKeysApi.reducerPath]: scannerKeysApi.reducer,
    [binsApi.reducerPath]: binsApi.reducer,
    [scannerTypesApi.reducerPath]: scannerTypesApi.reducer,
    events: eventsReducer,
    users: usersReducer,
    businesses: businessReducer,
    orders: ordersReducer,
    tickets: ticketsReducer,
    auth: authReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware()
    .concat(ordersApi.middleware)
    .concat(eventsApi.middleware)
    .concat(ticketsApi.middleware)
    .concat(businessesApi.middleware)
    .concat(usersApi.middleware)
    .concat(couponsApi.middleware)
    .concat(referralsApi.middleware)
    .concat(commonApi.middleware)
    .concat(paymentsApi.middleware)
    .concat(bankAccountApi.middleware)
    .concat(wireApi.middleware)
    .concat(aiQueryApi.middleware)
    .concat(customerApi.middleware)
    .concat(listApi.middleware)
    .concat(scannerKeysApi.middleware)
    .concat(binsApi.middleware)
    .concat(scannerTypesApi.middleware),
});

const rootReducer = combineReducers({
  events: eventsReducer,
  users: usersReducer,
  businesses: businessReducer,
  orders: ordersReducer,
  tickets: ticketsReducer,
  auth: authReducer,
});

setupListeners(store.dispatch);

export {
  useCreateOrderMutation,
  useGetEventOrdersQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useGetEventOrdersExportQuery,
  useUpdateOrderItemMutation,
  useDeleteOrderItemMutation,
  useUpdateOrderStatusMutation,
  useDecodeQrCodeQuery,
  useUpdateUsedOrderItemMutation,
  useSendOrderEmailMutation,
  useGetOrderByCustomIdQuery,
  useGetSellerOrdersQuery,
  useGetSellerSummaryQuery,
  useGetOneSellerSummaryQuery,
  useGetOrderTotalsQuery,
  useGetEventOrdersReportQuery,
  useGetQRImageQuery,
  useGetOrderByIdQuery,
  useValidateTicketExclusiveMutation,
  useGetSellerSummaryXlsxMutation,
  useGetOrderReferralDataQuery,
  useGetEventSalesByUserQuery,
  useGetQuickSellerSummaryQuery,
  useGetEventOrderItemsQuery,
  useCreateBatchManualOrdersMutation,
} from './apis/orders';

export {
  useGetAllBusinessEventsQuery,
  useGetEventByIdQuery,
  useGetBusinessEventsQuery,
  useGetCurrentAgentQuery,
  useGetAllEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useGetEventUsersQuery,
  useAddUserToEventMutation,
  useDeleteUserEventMutation,
  useAddEventTransactionMutation,
  useDeleteEventTransactionMutation,
  useUpdateEventTransactionMutation,
  useGetEventTransactionsQuery,
  useGetEventCapacityQuery,
  useCountSellerQrCodeMutation,
  useGetSellerEventQrQuery,
  useLazyGetSellerEventQrQuery,
  useUpdateUserEventMutation,
  useGetEventBalanceQuery,
  useCreateWireTransferMutation,
  useImportSellersToEventMutation,
  useReserveSeatMutation,
  useFreeSeatMutation,
  useGetEventCashierReportQuery,
  useGetOneCashierReportQuery,
  useGetEventAssistanceReportQuery,
  useGetEventSellersReportQuery,
  useGetEventsByQueryQuery,
  useCreateAudienceDiscountMutation,
  useCloneEventMutation,
  useGetEventBySlugQuery,
  useGetAudienceDiscountsQuery,
  useUpdateAudienceDiscountMutation,
  useDeleteAudienceDiscountMutation,
  useCreateEventCashRegisterMutation,
  useGetEventCashRegistersQuery,
  useOpenEventCashRegisterMutation,
  useCloseEventCashRegisterMutation,
  useUpdateEventCashRegisterMutation,
  useDeleteEventCashRegisterMutation,
  useGetEventCashRegisterByIdQuery,
  useGetEventCashRegisterMovementsQuery,
  useCreateEventCashRegisterMovementMutation,
  useCreateShowtimeMutation,
  useUpdateShowtimeMutation,
  useDeleteShowtimeMutation,
  useGetShowtimesQuery,
  useGetEventSubscriptionQuery,
  useCreateEventSubscriptionMutation,
  useDeleteEventBySuscribedIdMutation,
  useGetCategoriesByEventIdQuery,
  useGetCategoriesQuery,
  useCreateEventCategoryMutation,
  useDeleteEventCategoryMutation,
  useGetEventResponsesTableQuery,
  useGetEventItemsExportQuery,
  useGetEventQuestionsQuery,
  useGetEventResponsesByOrderIdMutation,
  useGetEventsSummaryQuery,
  useGetEventSummaryPDFMutation,
  useGetEventsSummaryXlsxMutation,
  useGetEventStatsQuery,
} from './apis/events';

export {
  useGetCouponByIdQuery,
  useGetAllCouponsQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
  useCheckCouponValidationMutation,
} from './apis/coupons';

export {
  useGetReferralByIdQuery,
  useGetAllReferralsQuery,
  useCreateReferralMutation,
  useUpdateReferralMutation,
  useDeleteReferralMutation,
  useGetReferralNameByCodeQuery,
} from './apis/referrals';

export {
  useGetMPAuthLinkQuery,
  useUploadImageMutation,
} from './apis/common';

export {
  useGetAllTicketsQuery,
  useGetTicketByIdQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useDeleteTicketMutation,
  useReserveTicketsMutation,
  useCreateReservationMutation,
  useGetReservationAndSeatsQuery,
} from './apis/tickets';

export {
  useGetAllBusinessesQuery,
  useGetBusinessByIdQuery,
  useCreateBusinessMutation,
  useUpdateBusinessMutation,
  useGetBusinessUsersQuery,
  useGetBusinessInvitationsQuery,
  useCreateInvitationMutation,
  useGetEmailInvitationsQuery,
  useAcceptInvitationMutation,
  useRejectInvitationMutation,
  useGetBusinessBySubdomainQuery,
  useDeleteInvitationMutation,
  useRemoveUserFromBusinessMutation,
  useAuthorizeMercadoPagoMutation,
  useGetPaymentConfigQuery,
  useUpdateUserBusinessMutation,
  useDeletePaymentConfigMutation,
  useGetBusinessCashRegistersQuery,
  useCreateBusinessCashRegisterMutation,
  useUpdateBusinessCashRegisterMutation,
  useDeleteBusinessCashRegisterMutation,
  useGetBusinessCashRegisterByIdQuery,
  useGetBusinessMembersQuery,
  useCreateBusinessMemberMutation,
  useUpdateBusinessMemberMutation,
  useUpdateBusinessMemberStatusMutation,
  useExportMembersXlsxMutation,
  useLazyGetBusinessBySubdomainQuery,
  useCheckMemberStatusMutation,
  useGetSellerGroupsQuery,
  useUpdatePaymentConfigMutation,
  useDestroyBusinessMemberMutation,
} from './apis/business';

export {
  useLogInMutation,
  useGetUserByFirebaseIdQuery,
  useCreateUserMutation,
  useGetUserByConfirmationCodeQuery,
  useVerifyUserMutation,
  useGetUserBusinessesQuery,
  useValidateGoogleLoginMutation,
  useLazyGetUserByFirebaseIdQuery,
  useGetAllUserEventsQuery,
  useGetUserQuery,
  useSendRecoverPasswordEmailMutation,
  useResetPasswordMutation,
} from './apis/users';

export {
  useGetPaymentByIdQuery,
  useGetPaymentByOrderIdQuery,
  useGetAvanttiPaymentReportQuery,
  useGetEventPaymentReportQuery,
  useGetEventPaymentReportXlsxMutation,
} from './apis/payment';

export {
  useGetBankAccountsQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
} from './apis/bank-account';

export {
  useGetWiresRequestsQuery,
  useCreateWireRequestMutation,
  useCreateApprovedWireRequestMutation,
  useUpdateWireRequestMutation,
  useDeleteWireRequestMutation,
  useCreateTransferMutation,
  useApproveRequestMutation,
  useDownloadWiresMutation,
  useDeleteTransferMutation,
} from './apis/wire';

export {
  useGenerateAiQueryMutation,
} from './apis/ai-query';

export {
  useGetCustomersQuery,
  useEditCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomerGroupsQuery,
  useCreateCustomerGroupMutation,
  useEditCustomerGroupMutation,
  useDeleteCustomerGroupMutation,
} from './apis/customer';

export {
  useGetListsQuery,
  useCreateListMutation,
  useGetListByIdQuery,
  useGetListsByEventIdQuery,
  useGetListGuestsQuery,
  useCreateListGuestsMutation,
  useAcceptListGuestsMutation,
  useCreateAdminListMutation,
} from './apis/lists';

export {
  useUseScannerKeyMutation,
  useCreateScannerKeyMutation,
} from './apis/scanner-keys';

export {
  useGetBinDataQuery,
  useLazyGetBinDataQuery,
} from './apis/bins';
export type { BinData } from './apis/bins';

export {
  useGetScannerTypesQuery,
  useGetScannerTypeByIdQuery,
  useCreateScannerTypeMutation,
  useUpdateScannerTypeMutation,
  useDeleteScannerTypeMutation,
  useGetScannerTypeTicketsQuery,
  useAddTicketToScannerTypeMutation,
  useRemoveTicketFromScannerTypeMutation,
} from './apis/scanner-types';
export type { NavePaymentData } from './apis/orders';

export {
  setCreatedOrder, setOrderItemData, setPollData, setReservation, clearReservation, selectReservation,
} from './slices/orders';
export { setBusiness } from './slices/business';
export { setAuthToken, setSelectedBusiness, setUserBusinesses } from './slices/users';
export { setTickets, setTicketsSelected } from './slices/tickets';
export { logout, loginSuccess, loadAuth } from './slices/auth';
export type RootState = ReturnType<typeof rootReducer>;
